import { useMemo, useState, useCallback, useEffect } from "react";

import { AmplitudeFlagKey, ProductDonationRecurrenceInterval } from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { v4 } from "uuid";

import { SUGGESTED_AMOUNTS } from "../../../../../common/constants/suggestedAmounts";
import { setUserProperty } from "../../../../../common/helpers/amplitude";
import { useLoggedOrganization } from "../../../../../common/hooks/useLoggedOrganization";

import { getFromLocalStorage, setInLocalStorage } from "@/common/helpers/localStorage";
import { DonationFormAmountOutput, DonationFormFieldOutput } from "@/common/types/trpc";

type ExperimentVariant = "control" | "test_B";

const isBrowser = () => typeof window !== "undefined";

const getStoredVariant = (): ExperimentVariant | null => {
  if (!isBrowser()) {
    return null;
  }
  const stored = getFromLocalStorage?.("suggestedAmountsAbTestVariant");
  if (stored === "test_A") {
    saveVariantToStorage("test_B");
    return "test_B";
  }
  if (stored === "control" || stored === "test_B") {
    return stored;
  }
  return null;
};

const saveVariantToStorage = (variant: ExperimentVariant) => {
  if (!isBrowser()) {
    return;
  }
  setInLocalStorage?.("suggestedAmountsAbTestVariant", variant);
};

const getCypressOverride = (): ExperimentVariant | undefined => {
  if (!isBrowser() || !(window as Window & { Cypress?: unknown }).Cypress) {
    return undefined;
  }
  const override = (window as Window & { __SUGGESTED_AMOUNTS_EXPERIMENT__?: string }).__SUGGESTED_AMOUNTS_EXPERIMENT__;
  return override === "test_B" ? "test_B" : "control";
};

const resolveVariantAssignment = (): ExperimentVariant => {
  const cypressOverride = getCypressOverride();
  if (cypressOverride) {
    return cypressOverride;
  }
  const stored = getStoredVariant();
  if (stored) {
    return stored;
  }
  const assigned = Math.random() < 0.5 ? "control" : "test_B";
  saveVariantToStorage(assigned);
  return assigned;
};

interface SuggestedAmountsExperimentData {
  suggestedAmounts: number[];
  isOff: boolean;
  suggestedAmountsTransformed: number[];
  context?: Record<string, unknown> | null;
}

const getPunctualAmountsFromDefault = (donationFormField: DonationFormFieldOutput | undefined, isPreview: boolean) => {
  if (donationFormField?.donationFormAmounts?.length) {
    return donationFormField?.donationFormAmounts?.filter(
      (field) => field.recurrenceInterval === ProductDonationRecurrenceInterval.OneTime
    );
  } else {
    return isPreview ? getPunctualAmountsForPreview() : [];
  }
};

const getPunctualAmountsForPreview = () => {
  return SUGGESTED_AMOUNTS.OneTime.map((amount, index) => ({
    amount,
    isRecurrent: false,
    recurrenceInterval: ProductDonationRecurrenceInterval.OneTime,
    id: v4(),
    sortIndex: index,
  }));
};

const getPunctualAmountsFromQueryParams = (
  donationFormField: DonationFormFieldOutput | undefined,
  punctualAmountsQuery: string
) => {
  const parsedPunctualAmounts = JSON.parse(punctualAmountsQuery) as string[];

  return (
    donationFormField?.donationFormAmounts
      ?.filter((field) => field.recurrenceInterval === ProductDonationRecurrenceInterval.OneTime)
      .map((field, index) => {
        if (parsedPunctualAmounts[index] && !isNaN(parseInt(parsedPunctualAmounts[index] as string))) {
          return { ...field, amount: parseInt(parsedPunctualAmounts[index] as string) * 100 };
        }
        return field;
      }) || []
  );
};

const getMonthlyAmountsFromDefault = (donationFormField: DonationFormFieldOutput | undefined, isPreview: boolean) => {
  if (donationFormField?.donationFormAmounts?.length) {
    return donationFormField?.donationFormAmounts?.filter(
      (field) => field.recurrenceInterval === ProductDonationRecurrenceInterval.Monthly
    );
  }
  return isPreview ? getMonthlyAmountsForPreview() : [];
};

const getMonthlyAmountsForPreview = () => {
  return SUGGESTED_AMOUNTS.Monthly.map((amount, index) => ({
    amount,
    isRecurrent: true,
    recurrenceInterval: ProductDonationRecurrenceInterval.Monthly,
    id: v4(),
    sortIndex: index,
  }));
};

const getMonthlyAmountsFromQueryParams = (
  donationFormField: DonationFormFieldOutput | undefined,
  monthlyAmountsQuery: string
) => {
  const parsedMonthlyAmounts = JSON.parse(monthlyAmountsQuery) as string[];

  return (
    donationFormField?.donationFormAmounts
      ?.filter((field) => field.recurrenceInterval === ProductDonationRecurrenceInterval.Monthly)
      .map((field, index) => {
        if (parsedMonthlyAmounts[index] && !isNaN(parseInt(parsedMonthlyAmounts[index] as string))) {
          return { ...field, amount: parseInt(parsedMonthlyAmounts[index] as string) * 100 };
        }
        return field;
      }) || []
  );
};

const getSavedAmountsForFrequency = (
  donationFormField: DonationFormFieldOutput | undefined,
  recurrenceInterval: ProductDonationRecurrenceInterval
) => donationFormField?.donationFormAmounts?.filter((field) => field.recurrenceInterval === recurrenceInterval) ?? [];

const getQuarterlyAmountsFromDefault = (donationFormField: DonationFormFieldOutput | undefined, isPreview: boolean) => {
  if (donationFormField?.donationFormAmounts?.length) {
    return donationFormField?.donationFormAmounts?.filter(
      (field) => field.recurrenceInterval === ProductDonationRecurrenceInterval.Quarterly
    );
  }
  return isPreview ? getQuarterlyAmountsForPreview() : [];
};

const getQuarterlyAmountsForPreview = () => {
  return SUGGESTED_AMOUNTS.Quarterly.map((amount, index) => ({
    amount,
    isRecurrent: true,
    recurrenceInterval: ProductDonationRecurrenceInterval.Quarterly,
    id: v4(),
    sortIndex: index,
  }));
};

export const getYearlyAmountsFromDefault = (
  donationFormField: DonationFormFieldOutput | undefined,
  isPreview: boolean
) => {
  if (donationFormField?.donationFormAmounts?.length) {
    return donationFormField?.donationFormAmounts?.filter(
      (field) => field.recurrenceInterval === ProductDonationRecurrenceInterval.Yearly
    );
  }
  return isPreview ? getYearlyAmountsForPreview() : [];
};

const getYearlyAmountsForPreview = () => {
  return SUGGESTED_AMOUNTS.Yearly.map((amount, index) => ({
    amount,
    isRecurrent: true,
    recurrenceInterval: ProductDonationRecurrenceInterval.Yearly,
    id: v4(),
    sortIndex: index,
  }));
};

/**
 * Uses pre-computed suggested amounts from SSR based on the Amplitude feature flag value.
 * The backend has already computed all variants during SSR, so this hook just selects
 * the appropriate variant based on the flag value.
 *
 * @param amounts - The original amounts from the form.
 * @param transformedAmounts - Pre-computed transformed amounts from SSR (one array per variant).
 * @param isPreview - When true, return amounts as-is without applying experiment transform.
 * @returns The transformed amounts (or original amounts if no transformation needed).
 */
export const useGetSuggestedAmountsAbTest = (
  suggestedAmountsExperiment: SuggestedAmountsExperimentData | null | undefined,
  donationFormField: DonationFormFieldOutput | undefined,
  isPreview = false
) => {
  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());
  const punctualAmountsQuery = decodeURI(query.punctualAmounts as string);
  const monthlyAmountsQuery = decodeURI(query.monthlyAmounts as string);
  const { isConnectedOnDashboard } = useLoggedOrganization();
  const [lastInteractionWasClickedAmount, setLastInteractionWasClickedAmount] = useState(false);
  const [lastClickedAmountWasIndex, setLastClickedAmountWasIndex] = useState<number | undefined>(undefined);

  // We default to "control" on the server to keep SSR deterministic and avoid
  // hydration mismatches. The real variant is assigned on the client only,
  // inside the effect below.
  const [experimentVariantAssignment, setExperimentVariantAssignment] = useState<ExperimentVariant>(() =>
    isBrowser() ? resolveVariantAssignment() : "control"
  );

  useEffect(() => {
    if (!isBrowser()) {
      return;
    }
    setExperimentVariantAssignment((current) => {
      const resolved = resolveVariantAssignment();
      return resolved === current ? current : resolved;
    });
  }, []);

  const ponctualAmounts: DonationFormAmountOutput[] = useMemo(() => {
    if (punctualAmountsQuery === "undefined") {
      return getPunctualAmountsFromDefault(donationFormField, isPreview);
    } else {
      return getPunctualAmountsFromQueryParams(donationFormField, punctualAmountsQuery);
    }
  }, [donationFormField, isPreview, punctualAmountsQuery]);

  const monthlyAmounts: DonationFormAmountOutput[] = useMemo(() => {
    if (monthlyAmountsQuery === "undefined") {
      return getMonthlyAmountsFromDefault(donationFormField, isPreview);
    }
    return getMonthlyAmountsFromQueryParams(donationFormField, monthlyAmountsQuery);
  }, [donationFormField, isPreview, monthlyAmountsQuery]);

  const weeklyAmounts: DonationFormAmountOutput[] = useMemo(
    () => getSavedAmountsForFrequency(donationFormField, ProductDonationRecurrenceInterval.Weekly),
    [donationFormField]
  );

  const biWeeklyAmounts: DonationFormAmountOutput[] = useMemo(
    () => getSavedAmountsForFrequency(donationFormField, ProductDonationRecurrenceInterval.BiWeekly),
    [donationFormField]
  );

  const quarterlyAmounts: DonationFormAmountOutput[] = useMemo(() => {
    return getQuarterlyAmountsFromDefault(donationFormField, isPreview);
  }, [donationFormField, isPreview]);

  const yearlyAmounts: DonationFormAmountOutput[] = useMemo(() => {
    return getYearlyAmountsFromDefault(donationFormField, isPreview);
  }, [donationFormField, isPreview]);

  const isExperimentOff = !suggestedAmountsExperiment || suggestedAmountsExperiment.isOff;
  const hasPonctualSuggestedAmounts = ponctualAmounts.some((amount) => amount.amount > 0);
  const hideExperiment = isExperimentOff || isConnectedOnDashboard || isPreview || !hasPonctualSuggestedAmounts;

  const { transformedPonctualAmounts, suggestedAmountsExperimentData } = getSuggestedAmountsExperimentData(
    suggestedAmountsExperiment,
    ponctualAmounts,
    hideExperiment,
    experimentVariantAssignment
  );

  const suggestedAmounts = [
    ...transformedPonctualAmounts,
    ...weeklyAmounts,
    ...biWeeklyAmounts,
    ...monthlyAmounts,
    ...quarterlyAmounts,
    ...yearlyAmounts,
  ];

  useEffect(() => {
    setUserProperty(AmplitudeFlagKey.SuggestedAmountsTest, suggestedAmountsExperimentData.experimentVariant);
  }, [suggestedAmountsExperimentData.experimentVariant]);

  const handleSuggestedAmountClicked = useCallback((index: number) => {
    setLastInteractionWasClickedAmount(true);
    setLastClickedAmountWasIndex(index);
  }, []);

  const handleManualAmountInputChanged = useCallback(() => {
    setLastInteractionWasClickedAmount(false);
  }, []);

  return {
    suggestedAmountsExperimentData,
    suggestedAmounts,
    lastInteractionWasClickedAmount,
    lastClickedAmountWasIndex,
    handleSuggestedAmountClicked,
    handleManualAmountInputChanged,
  };
};

const getSuggestedAmountsExperimentData = (
  suggestedAmountsExperiment: SuggestedAmountsExperimentData | null | undefined,
  ponctualAmounts: DonationFormAmountOutput[],
  hideExperiment: boolean,
  variantAssignment: ExperimentVariant
): {
  transformedPonctualAmounts: DonationFormAmountOutput[];
  suggestedAmountsExperimentData: {
    experimentName: string;
    experimentVariant: string;
    experimentInputAmounts: number[];
    experimentInputContext: Record<string, unknown> | undefined;
    experimentOutput: number[];
  };
} => {
  const isExperimentOff = !suggestedAmountsExperiment || suggestedAmountsExperiment.isOff;
  if (hideExperiment || isExperimentOff) {
    return {
      transformedPonctualAmounts: ponctualAmounts,
      suggestedAmountsExperimentData: {
        experimentName: AmplitudeFlagKey.SuggestedAmountsTest,
        experimentVariant: "off",
        experimentInputAmounts: ponctualAmounts.map((amount) => amount.amount),
        experimentInputContext: undefined,
        experimentOutput: ponctualAmounts.map((amount) => amount.amount),
      },
    };
  }

  if (variantAssignment === "control") {
    return {
      transformedPonctualAmounts: ponctualAmounts,
      suggestedAmountsExperimentData: {
        experimentName: AmplitudeFlagKey.SuggestedAmountsTest,
        experimentVariant: "control",
        experimentInputAmounts: ponctualAmounts.map((amount) => amount.amount),
        experimentInputContext: suggestedAmountsExperiment?.context ?? undefined,
        experimentOutput: ponctualAmounts.map((amount) => amount.amount),
      },
    };
  }

  // Test variant: use transformed amounts
  const transformedPonctualAmounts = ponctualAmounts.map((amount, idx) => {
    const transformedAmount = suggestedAmountsExperiment?.suggestedAmountsTransformed[idx];
    return { ...amount, amount: transformedAmount || amount.amount };
  });

  return {
    transformedPonctualAmounts,
    suggestedAmountsExperimentData: {
      experimentName: AmplitudeFlagKey.SuggestedAmountsTest,
      experimentVariant: "test_B",
      experimentInputAmounts: ponctualAmounts.map((amount) => amount.amount),
      experimentInputContext: suggestedAmountsExperiment?.context ?? undefined,
      experimentOutput: transformedPonctualAmounts.map((amount) => amount.amount),
    },
  };
};
